// File: frontend/src/layouts/Header.tsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import "./Header.css";
import { RegisterModal } from "../components/registration/RegisterModal";
import { LoginModal } from "../components/registration/LoginModal";
import { ForgotPasswordModal } from "../components/registration/ForgotPasswordModal";
import { useAuth } from "../context/AuthContext";
import { useTheme, ThemeType } from "../context/ThemeContext";
import { usePreferences } from "../context/PreferencesContext";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { useTranslation } from "react-i18next";
import { generateProfileUrl } from "../utils/slugGenerator";
import { ThemeEnum } from "../models/enums/ThemeEnum";
import { SearchBar } from "../components/search/SearchBar";
import { NotificationBell } from "../components/notifications/NotificationBell";
import { onMessageEvent } from "../services/websocketService";
import { getUnreadCount } from "../services/messageService";
import { getPendingSuggestionCount, PENDING_SUGGESTION_COUNT_EVENT } from "../services/adminCustomTopicService";
import { OnlineUsersModal } from "../components/online/OnlineUsersModal";
import { UserStatsModal } from "../components/statistics";

// Helper to get NEXT theme in cycle
// "taraftar" only joins the cycle when the user actually has a favourite club
// with colours on record — otherwise the theme has nothing to paint with and
// must stay invisible, exactly like in the Settings picker.
const getNextTheme = (current: ThemeType, fanThemeAvailable: boolean): ThemeType => {
  const themeOrder: ThemeType[] = ["light", "dark", "paper", "tango", "flamingo", "comolokko", "lokum", "mercan", "gokyuzu"];
  if (fanThemeAvailable) {
    themeOrder.push("taraftar");
  }
  const currentIndex = themeOrder.indexOf(current);
  const nextIndex = (currentIndex + 1) % themeOrder.length;
  return themeOrder[nextIndex];
};

// Theme icon component - shows the NEXT theme icon
const ThemeIcon: React.FC<{ nextTheme: ThemeType }> = ({ nextTheme }) => {
  switch (nextTheme) {
    case "dark":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
          <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z"/>
        </svg>
      );
    case "paper":
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
          <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
        </svg>
      );
    case "tango":
      // Sun-and-stripes icon for the Argentina flag inspired theme
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
          <circle cx="12" cy="12" r="4"/>
          <path d="M2 4h20v3H2zM2 17h20v3H2z"/>
        </svg>
      );
    case "flamingo":
      // Heart icon for the pink theme
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
        </svg>
      );
    case "comolokko":
      // Wave icon for the deep navy/cyan theme
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
          <path d="M2 12c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 4-2v3c-2 0-2 2-4 2s-2-2-4-2-2 2-4 2-2-2-4-2-2 2-4 2v-3zM2 17c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 4-2v3c-2 0-2 2-4 2s-2-2-4-2-2 2-4 2-2-2-4-2-2 2-4 2v-3z"/>
        </svg>
      );
    case "lokum":
      // Turkish delight cube icon for the pink/lavender theme
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
          <rect x="4" y="4" width="16" height="16" rx="4"/>
        </svg>
      );
    case "mercan":
      // Coral fish icon for the cream/coral/navy theme
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
          <ellipse cx="10" cy="12" rx="7" ry="4.5"/>
          <path d="M16 12l6-4.5v9z"/>
        </svg>
      );
    case "gokyuzu":
      // Cloud icon (Material "cloud") for the sky theme
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
          <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"/>
        </svg>
      );
    case "taraftar":
      // Shield icon for the club-coloured fan theme
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
          <path d="M12 2l8 3v6c0 5-3.4 9.2-8 11-4.6-1.8-8-6-8-11V5l8-3z"/>
        </svg>
      );
    case "light":
    default:
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
          <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z"/>
        </svg>
      );
  }
};

interface HeaderProps {
  // Optional slot rendered to the right of the main search bar. App.tsx
  // mounts the PaneFilterBar here on the main layout, so the filters sit
  // next to the search where users always look — not tucked into a corner.
  filterSlot?: React.ReactNode;
  // Fired when the user navigates home via the brand/logo. App.tsx uses it
  // to flip the MOBILE view back to the list pane — without it, the "/"
  // redirect lands on the default topic's content page and the user has to
  // press back on every return to the home screen.
  onHomeNavigate?: () => void;
}

const HeaderComponent: React.FC<HeaderProps> = ({ filterSlot, onHomeNavigate }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const { theme, setTheme, fanThemeAvailable } = useTheme();
  const { updateUserPreferences } = usePreferences();
  // Read dynamic site name and slogan from the settings context
  const { siteName, siteSlogan } = useSiteSettings();
  const navigate = useNavigate();
  
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isOnlineModalOpen, setIsOnlineModalOpen] = useState(false);
  // Personal statistics modal — replaces the old always-visible UserStatsBar
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  
  const userMenuRef    = useRef<HTMLDivElement>(null);
  const onlineButtonRef = useRef<HTMLButtonElement>(null);

  // Unread message count for header badge
  const [unreadMessages, setUnreadMessages] = useState(0);

  // Load initial unread count and listen for real-time updates
  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadMessages(0);
      return;
    }

    // Fetch initial unread count
    getUnreadCount()
      .then((data) => setUnreadMessages(data.totalUnread))
      .catch(() => {});

    // Subscribe to real-time message events for badge updates
    const unsubscribe = onMessageEvent((event) => {
      if (event.totalUnread !== null && event.totalUnread !== undefined) {
        setUnreadMessages(event.totalUnread);
      }
    });

    return unsubscribe;
  }, [isAuthenticated]);

  // ── Pending topic suggestion alert (ADMIN + MODERATOR only) ──
  const location = useLocation();
  const headerRole = user?.role?.toUpperCase();
  const canReviewSuggestions = isAuthenticated && (headerRole === "ADMIN" || headerRole === "MODERATOR");
  const [pendingSuggestions, setPendingSuggestions] = useState(0);

  // Keep the count in sync: every fetch (here or in the admin panel) is
  // broadcast as a window event; returning to the browser tab triggers a refetch.
  // Event-driven only — no interval polling.
  useEffect(() => {
    if (!canReviewSuggestions) {
      setPendingSuggestions(0);
      return;
    }

    const handleCount = (event: Event) => {
      const count = (event as CustomEvent<number>).detail;
      if (typeof count === "number") setPendingSuggestions(count);
    };
    const handleFocus = () => {
      // Alert is cosmetic — ignore fetch errors silently
      getPendingSuggestionCount().catch(() => {});
    };

    window.addEventListener(PENDING_SUGGESTION_COUNT_EVENT, handleCount);
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener(PENDING_SUGGESTION_COUNT_EVENT, handleCount);
      window.removeEventListener("focus", handleFocus);
    };
  }, [canReviewSuggestions]);

  // Refetch on every page change (also covers the initial mount); the result
  // reaches the state through the broadcast listener above
  useEffect(() => {
    if (!canReviewSuggestions) return;
    getPendingSuggestionCount().catch(() => {});
  }, [canReviewSuggestions, location.pathname]);

  // Jump straight to the suggestion review list in the admin panel
  const handlePendingSuggestionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUserMenuOpen(false);
    navigate("/admin?tab=custom-topics");
  };

  // Navigate to messages page
  const handleMessagesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate("/mesajlar");
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Navigate to home page
  const handleHeaderClick = () => {
    // Clicking the header background counts as going home too — flip the
    // mobile view back to the list, same as the brand/logo click.
    onHomeNavigate?.();
    navigate("/");
  };

  // Open register modal
  const openRegisterModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsRegisterModalOpen(true);
  };

  // Open login modal
  const openLoginModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLoginModalOpen(true);
  };

  // Handle user logout
  const handleLogout = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUserMenuOpen(false);
    logout();
    navigate("/");
  };

  // Toggle user menu
  const toggleUserMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  // Navigate to profile page
  const handleProfileClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUserMenuOpen(false);
    if (user) {
      navigate(generateProfileUrl(user.username, user.id));
    }
  };

  // Navigate to settings page
  const handleSettingsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUserMenuOpen(false);
    navigate("/ayarlar");
  };

  // Navigate to statistics page
  const handleStatisticsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUserMenuOpen(false);
    navigate("/istatistikler");
  };

  // Navigate to contact page
  const handleContactClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUserMenuOpen(false);
    navigate("/iletisim");
  };

  // Navigate to personal trash page
  const handleTrashClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUserMenuOpen(false);
    navigate("/cop-kutusu");
  };

  // Navigate to personal drafts page
  const handleDraftsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUserMenuOpen(false);
    navigate("/taslaklar");
  };

  // Navigate to moderation log page
  const handleModlogClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUserMenuOpen(false);
    navigate("/moderasyon-gunlugu");
  };

  // Navigate to activity feed page
  const handleFeedClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUserMenuOpen(false);
    navigate("/akis");
  };

  // Cycle through themes: light → dark → paper → tango → flamingo → comolokko
  // → lokum → mercan → gokyuzu → (taraftar, if available) → light
  const handleThemeCycle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    const newTheme = getNextTheme(theme, fanThemeAvailable);
    
    // Map to backend enum
    const themeMap: Record<ThemeType, ThemeEnum> = {
      "light": ThemeEnum.LIGHT,
      "dark": ThemeEnum.DARK,
      "paper": ThemeEnum.PAPER,
      "tango": ThemeEnum.TANGO,
      "flamingo": ThemeEnum.FLAMINGO,
      "comolokko": ThemeEnum.COMOLOKKO,
      "lokum": ThemeEnum.LOKUM,
      "mercan": ThemeEnum.MERCAN,
      "gokyuzu": ThemeEnum.GOKYUZU,
      "taraftar": ThemeEnum.TARAFTAR,
    };
    
    // Update frontend theme immediately
    setTheme(newTheme);
    
    // Update PreferencesContext (which also syncs to backend) if authenticated
    if (isAuthenticated) {
      try {
        await updateUserPreferences({ theme: themeMap[newTheme] });
      } catch (err) {
        console.error("Failed to sync theme to backend:", err);
      }
    }
  };

  // Navigate to admin panel
  const handleAdminPanelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUserMenuOpen(false);
    navigate("/admin");
  };

  // Switch from register modal to login modal
  const switchToLogin = () => {
    setIsRegisterModalOpen(false);
    setIsLoginModalOpen(true);
  };

  // Switch from login modal to register modal
  const switchToRegister = () => {
    setIsLoginModalOpen(false);
    setIsRegisterModalOpen(true);
  };

  // Open forgot-password modal from login modal
  const switchToForgotPassword = () => {
    setIsLoginModalOpen(false);
    setIsForgotPasswordOpen(true);
  };

  // Get CSS class for user role (avatar badge in header)
  const getStatusClass = (role: string): string => {
    switch (role.toUpperCase()) {
      case "ADMIN":     return "status-admin";
      case "MODERATOR": return "status-moderator";
      case "AUTHOR":    return "status-author";
      case "ROOKIE":    return "status-rookie";
      default:          return "status-rookie";
    }
  };

  // Get display name for user role (shown in avatar tooltip)
  const getStatusDisplayName = (role: string): string => {
    switch (role.toUpperCase()) {
      case "ADMIN":     return t("status_admin");
      case "MODERATOR": return t("status_moderator");
      case "AUTHOR":    return t("status_author");
      case "ROOKIE":    return t("status_rookie");
      default:          return t("status_rookie");
    }
  };


  // Check if user is admin or moderator (based on role)
  const isAdminOrModerator = (role: string): boolean => {
    const upper = role.toUpperCase();
    return upper === "ADMIN" || upper === "MODERATOR";
  };

  // Get next theme for display
  const nextTheme = getNextTheme(theme, fanThemeAvailable);

  return (
    <>
      <header className="header" onClick={handleHeaderClick}>
        {/* Site branding: logo + title + slogan — Link wrapper enables right-click "Open in new tab" */}
        <Link to="/" className="header-brand" onClick={(e) => { if (!e.ctrlKey && !e.metaKey) { e.preventDefault(); onHomeNavigate?.(); navigate("/"); } }}>
          {/* Site logo image — dedicated light-on-dark variant so it stays
              readable against every theme's header background color */}
          <img
            src="/logo-header.svg"
            alt={siteName}
            className="header-logo"
          />

          <div className="header-brand-text">
            {/* The site name is the page's <h1> on the home page only — the home
                page has no heading of its own, and search engines flag a page
                without an <h1> (Bing Site Scan: "H1 tag missing"). Every other
                page renders its own <h1> (topic, club, person, ...), so the
                header stays a <span> there to keep exactly one <h1> per page. */}
            {location.pathname === "/" ? (
              <h1 className="header-title">{siteName}</h1>
            ) : (
              <span className="header-title">{siteName}</span>
            )}
            <span className="header-slogan">{siteSlogan}</span>
          </div>
        </Link>

        {/* Center: search bar — stops click propagation so it doesn't navigate home */}
        <div className="header-search-center" onClick={(e) => e.stopPropagation()}>
          {/* Search and the filter slot share one row: the filters live right
              next to the search where they are always visible, instead of a
              far corner the user has to hunt for. */}
          <div className="header-search-row">
            <SearchBar />
            {filterSlot && (
              <div className="header-filter-slot">
                {filterSlot}
              </div>
            )}
          </div>
        </div>
        
        <div className="header-buttons">
          {isAuthenticated && user ? (
            <div className="user-section">
              {/* Personal statistics button — opens the stats modal on demand
                  (the ranking call now fires only when this is clicked) */}
              <button
                className="header-stats-btn"
                onClick={(e) => { e.stopPropagation(); setIsStatsModalOpen(true); }}
                title={t("stats_modal_title", "İstatistiklerim")}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
                </svg>
              </button>

              {/* Messages Button */}
              <button
                className="header-messages-btn"
                onClick={handleMessagesClick}
                title={t("messages_title")}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                {unreadMessages > 0 && (
                  <span className="header-messages-badge">
                    {unreadMessages > 99 ? "99+" : unreadMessages}
                  </span>
                )}
              </button>

              {/* Notification Bell */}
              <NotificationBell />

              {/* Online Users Button */}
              <button
                ref={onlineButtonRef}
                className="header-online-btn"
                onClick={(e) => { e.stopPropagation(); setIsOnlineModalOpen(true); }}
                title={t("online_users_title")}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                </svg>
                <span className="header-online-dot" aria-hidden="true" />
              </button>

              {/* Pending topic suggestion alert — ADMIN + MODERATOR, only while
                  at least one suggestion waits for review */}
              {canReviewSuggestions && pendingSuggestions > 0 && (
                <button
                  className="header-pending-suggestions-btn"
                  onClick={handlePendingSuggestionsClick}
                  title={t("header_pending_topic_suggestions", { total: pendingSuggestions })}
                  aria-label={t("header_pending_topic_suggestions", { total: pendingSuggestions })}
                >
                  {/* Hashtag icon — same visual pattern as the other header icons */}
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
                    <path d="M20 10V8h-4V4h-2v4h-4V4H8v4H4v2h4v4H4v2h4v4h2v-4h4v4h2v-4h4v-2h-4v-4h4zm-6 4h-4v-4h4v4z"/>
                  </svg>
                  <span className="header-pending-suggestions-badge">
                    {pendingSuggestions > 99 ? "99+" : pendingSuggestions}
                  </span>
                </button>
              )}

              {/* Admin Panel Button - only for ADMIN and MODERATOR */}
              {isAdminOrModerator(user.role) && (
                <button className="admin-panel-btn" onClick={handleAdminPanelClick} title={t("user_menu_admin_panel")}>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                  </svg>
                </button>
              )}

              {/* Clickable profile link — border color reflects user role */}
              <button className={`user-profile-link user-profile-link--${user.role.toLowerCase()}`} onClick={handleProfileClick}>
                <span 
                  className={`user-avatar ${getStatusClass(user.role)}`}
                  title={getStatusDisplayName(user.role)}
                >
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user.username}
                      className="user-avatar-img"
                    />
                  ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  )}
                </span>
                <span className="user-profile-text">
                  {/* The box shows "ben" instead of the username
                      (long usernames would not fit); the full
                      username is still revealed via title on hover */}
                  <span className="user-profile-username" title={user.username}>{t("header_profile_me", "ben")}</span>
                  <span
                    className="user-profile-role"
                    title={getStatusDisplayName(user.role)}
                  >
                    {getStatusDisplayName(user.role)}
                  </span>
                </span>
              </button>

              {/* Burger menu */}
              <div className="burger-menu-container" ref={userMenuRef}>
                <button className="burger-menu-trigger" onClick={toggleUserMenu}>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26">
                    <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
                  </svg>
                </button>
                
                {isUserMenuOpen && (
                  <div className="burger-menu-dropdown">
                    {/* Theme toggle button - shows NEXT theme */}
                    <button 
                      className="burger-menu-item" 
                      onClick={handleThemeCycle}
                    >
                      <ThemeIcon nextTheme={nextTheme} />
                      <span>{t(`theme_${nextTheme}`)}</span>
                    </button>
                    <div className="burger-menu-divider"></div>
                    <button className="burger-menu-item" onClick={handleFeedClick}>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                        <path d="M3 5h12v2H3V5zm0 4h12v2H3V9zm0 4h8v2H3v-2zm14 3l5-5-1.41-1.41L17 13.17l-2.59-2.58L13 12l4 4z"/>
                      </svg>
                      <span>{t("user_menu_feed")}</span>
                    </button>
                    <div className="burger-menu-divider"></div>
                    <button className="burger-menu-item" onClick={handleModlogClick}>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-1 16h2v2h-2v-2zm0-10h2v8h-2V7z"/>
                      </svg>
                      <span>{t("user_menu_modlog")}</span>
                    </button>
                    <div className="burger-menu-divider"></div>
                    <button className="burger-menu-item" onClick={handleDraftsClick}>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                      </svg>
                      <span>{t("user_menu_drafts")}</span>
                    </button>
                    <div className="burger-menu-divider"></div>
                    <button className="burger-menu-item" onClick={handleTrashClick}>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                        <path d="M15 4V3H9v1H4v2h1l1 13c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2l1-13h1V4h-5zm2 15H7L6.05 6h11.9L17 19z"/>
                      </svg>
                      <span>{t("user_menu_trash")}</span>
                    </button>
                    <div className="burger-menu-divider"></div>
                    <button className="burger-menu-item" onClick={handleStatisticsClick}>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                        <path d="M5 9.2h3V19H5V9.2zM10.6 5h2.8v14h-2.8V5zm5.6 8H19v6h-2.8v-6z"/>
                      </svg>
                      <span>{t("user_menu_statistics")}</span>
                    </button>
                    <div className="burger-menu-divider"></div>
                    {/* Contact page — visible to all authenticated users */}
                    <button className="burger-menu-item" onClick={handleContactClick}>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                        <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-1 14H5c-.55 0-1-.45-1-1V7l7 5 7-5v10c0 .55-.45 1-1 1zm-7-7L4 6h16l-8 5z"/>
                      </svg>
                      <span>{t("user_menu_contact")}</span>
                    </button>
                    <div className="burger-menu-divider"></div>
                    <button className="burger-menu-item" onClick={handleSettingsClick}>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                        <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
                      </svg>
                      <span>{t("user_menu_settings")}</span>
                    </button>
                    <div className="burger-menu-divider"></div>
                    <button className="burger-menu-item burger-menu-item-logout" onClick={handleLogout}>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                        <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
                      </svg>
                      <span>{t("user_menu_logout")}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Theme toggle for non-authenticated users */}
              <button 
                className="header-btn header-theme-btn" 
                onClick={handleThemeCycle}
                title={t(`theme_${nextTheme}`)}
              >
                <ThemeIcon nextTheme={nextTheme} />
              </button>
              <button className="header-btn" onClick={openLoginModal}>
                {t("header_login")}
              </button>
              <button className="header-btn" onClick={openRegisterModal}>
                {t("header_register")}
              </button>
            </>
          )}
        </div>
      </header>

      <RegisterModal
        isOpen={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        onSwitchToLogin={switchToLogin}
      />

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onSwitchToRegister={switchToRegister}
        onForgotPassword={switchToForgotPassword}
      />

      <ForgotPasswordModal
        isOpen={isForgotPasswordOpen}
        onClose={() => setIsForgotPasswordOpen(false)}
        onSwitchToLogin={() => { setIsForgotPasswordOpen(false); setIsLoginModalOpen(true); }}
      />

      {/* Online Users Modal */}
      {isOnlineModalOpen && (
        <OnlineUsersModal
          triggerRef={onlineButtonRef}
          onClose={() => setIsOnlineModalOpen(false)}
        />
      )}

      {/* Personal Statistics Modal */}
      {isStatsModalOpen && (
        <UserStatsModal onClose={() => setIsStatsModalOpen(false)} />
      )}
    </>
  );
};

// Header takes no props — memoized so it does not re-render when the parent AppContent
// re-renders (Yenile refresh, new-entry poll, pane switches). Its own state/context
// (unread message badge, auth) still updates normally.
export const Header = React.memo(HeaderComponent);